import yaml from 'js-yaml';

export interface ParsedSpec {
    sourceType: string;
    connectionName: string;
    description: string;
    baseUrl: string;
    authType: string;
    authDetails: Record<string, string>;
    endpointCount: number;
    fileName: string;
    settings: Record<string, any>;
}

function countEndpoints(paths: Record<string, any> | undefined): number {
    if (!paths) return 0;
    let count = 0;
    for (const path of Object.values(paths)) {
        if (typeof path === 'object' && path !== null) {
            for (const key of Object.keys(path)) {
                if (['get', 'post', 'put', 'patch', 'delete', 'head', 'options'].includes(key)) {
                    count++;
                }
            }
        }
    }
    return count;
}

function extractAuthFromSchemes(schemes: Record<string, any> | undefined): {
    authType: string;
    authDetails: Record<string, string>;
} {
    if (!schemes) return {
 authType: 'none',
authDetails: {}
};

    for (const [, scheme] of Object.entries(schemes)) {
        if (!scheme || typeof scheme !== 'object') continue;

        const type = scheme.type?.toLowerCase();
        const schemeValue = scheme.scheme?.toLowerCase();

        if (type === 'http' && schemeValue === 'bearer') {
            return {
 authType: 'bearer',
authDetails: {}
};
        }
        if (type === 'http' && schemeValue === 'basic') {
            return {
 authType: 'basic',
authDetails: {}
};
        }
        if (type === 'apikey' || type === 'apiKey') {
            return {
                authType: 'api_key',
                authDetails: {
                    api_key_name: scheme.name || 'X-API-Key',
                    api_key_in: scheme.in || 'header',
                },
            };
        }
        if (type === 'oauth2') {
            const flows = scheme.flows || {};
            const flowType = Object.keys(flows)[0];
            const flow = flows[flowType] || {};
            return {
                authType: 'oauth2',
                authDetails: {
                    oauth2_token_url: flow.tokenUrl || '',
                },
            };
        }
    }

    return {
 authType: 'none',
authDetails: {}
};
}

function parseOpenAPIv3(spec: any, fileName: string): ParsedSpec {
    const info = spec.info || {};
    const servers = spec.servers || [];
    const baseUrl = servers[0]?.url || '';

    const securitySchemes = spec.components?.securitySchemes || {};
    const {
 authType, authDetails
} = extractAuthFromSchemes(securitySchemes);

    return {
        sourceType: 'rest',
        connectionName: info.title || fileName.replace(/\.[^.]+$/, ''),
        description: info.description || '',
        baseUrl,
        authType,
        authDetails,
        endpointCount: countEndpoints(spec.paths),
        fileName,
        settings: {
 base_url: baseUrl
},
    };
}

function parseOpenAPIv2(spec: any, fileName: string): ParsedSpec {
    const info = spec.info || {};
    const scheme = (spec.schemes || ['https'])[0];
    const host = spec.host || '';
    const basePath = spec.basePath || '';
    const baseUrl = host ? `${scheme}://${host}${basePath}` : '';

    const securityDefs = spec.securityDefinitions || {};
    const {
 authType, authDetails
} = extractAuthFromSchemes(securityDefs);

    return {
        sourceType: 'rest',
        connectionName: info.title || fileName.replace(/\.[^.]+$/, ''),
        description: info.description || '',
        baseUrl,
        authType,
        authDetails,
        endpointCount: countEndpoints(spec.paths),
        fileName,
        settings: {
 base_url: baseUrl
},
    };
}

function countPostmanItems(items: any[]): number {
    let count = 0;
    for (const item of items) {
        if (item.request) count++;
        if (Array.isArray(item.item)) count += countPostmanItems(item.item);
    }
    return count;
}

function parsePostmanCollection(spec: any, fileName: string): ParsedSpec {
    const info = spec.info || {};
    const variables = spec.variable || [];
    const baseUrlVar = variables.find((v: any) => v.key === 'baseUrl' || v.key === 'base_url');
    const baseUrl = baseUrlVar?.value || '';

    let authType = 'none';
    const authDetails: Record<string, string> = {};
    const auth = spec.auth;
    if (auth) {
        authType = auth.type?.toLowerCase() || 'none';
        if (authType === 'bearer' && Array.isArray(auth.bearer)) {
            // Postman stores bearer tokens in auth.bearer array
        } else if (authType === 'apikey' && Array.isArray(auth.apikey)) {
            authType = 'api_key';
            for (const param of auth.apikey) {
                if (param.key === 'key') authDetails.api_key_name = param.value || '';
                if (param.key === 'in') authDetails.api_key_in = param.value || 'header';
            }
        }
    }

    const items = spec.item || [];

    return {
        sourceType: 'rest',
        connectionName: info.name || fileName.replace(/\.[^.]+$/, ''),
        description: info.description || '',
        baseUrl,
        authType,
        authDetails,
        endpointCount: countPostmanItems(items),
        fileName,
        settings: {
 base_url: baseUrl
},
    };
}

export function parseApiSpec(content: string, fileName: string): ParsedSpec {
    let parsed: any;

    // Try JSON first, then YAML
    try {
        parsed = JSON.parse(content);
    } catch {
        try {
            parsed = yaml.load(content);
        } catch (yamlError: any) {
            throw new Error(`Failed to parse file. Ensure it is valid JSON or YAML. (${yamlError.message})`);
        }
    }

    if (!parsed || typeof parsed !== 'object') {
        throw new Error('File does not contain a valid specification object.');
    }

    // Detect format
    if (parsed.openapi && typeof parsed.openapi === 'string' && parsed.openapi.startsWith('3')) {
        return parseOpenAPIv3(parsed, fileName);
    }

    if (parsed.swagger && typeof parsed.swagger === 'string' && parsed.swagger.startsWith('2')) {
        return parseOpenAPIv2(parsed, fileName);
    }

    if (parsed.info?._postman_id || (Array.isArray(parsed.item) && parsed.info?.name)) {
        return parsePostmanCollection(parsed, fileName);
    }

    throw new Error(
        'Unrecognized file format. Supported formats: OpenAPI v2 (Swagger), OpenAPI v3, and Postman Collections.'
    );
}
